// Popping the cash drawer. A drawer is almost always wired to the receipt printer, which opens it when it gets a short "kick" command.
// A web page cannot talk to a printer directly, so there are two ways, chosen once on each register computer (the setting is kept on that computer):
//   serial: the printer or drawer shows up as a serial (COM) port. The browser asks once which port, then the kick is sent each time.
//   print:  a tiny blank page is printed. If the printer's driver is set to open the drawer when it prints, the drawer opens.
//           Chrome shows a print window every time unless it is started with --kiosk-printing.

export type DrawerMethod = 'off' | 'serial' | 'print';
export interface DrawerConfig { method: DrawerMethod; baud: number; onCash: boolean; portLabel?: string; vid?: number; pid?: number }

const KEY = 'njpos.drawer';
const DEFAULT: DrawerConfig = { method: 'off', baud: 9600, onCash: true };

export function loadDrawer(): DrawerConfig {
  try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; } catch { return DEFAULT; }
}
export function saveDrawer(c: DrawerConfig) { try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* storage blocked */ } }

const serialApi = () => (navigator as any).serial as { requestPort(o?: unknown): Promise<any>; getPorts(): Promise<any[]> } | undefined;
export const serialSupported = () => !!serialApi();

/** ESC/POS "open drawer": pulse pin 2, then pin 5 (a drawer is on one or the other). Works on Epson, Star in ESC/POS mode, Bixolon, Citizen and most others. */
export const KICK = new Uint8Array([0x1b, 0x70, 0x00, 0x40, 0x50, 0x1b, 0x70, 0x01, 0x40, 0x50]);

/** Ask the person which port the printer or drawer is on. The browser remembers the answer. */
export async function chooseSerialPort(): Promise<{ vid?: number; pid?: number; label: string }> {
  const s = serialApi();
  if (!s) throw new Error('This browser cannot talk to a serial port. Use Chrome or Edge on the register computer.');
  const port = await s.requestPort();
  const info = port.getInfo?.() ?? {};
  return { vid: info.usbVendorId, pid: info.usbProductId, label: info.usbVendorId ? `USB device ${info.usbVendorId.toString(16)}:${(info.usbProductId ?? 0).toString(16)}` : 'Serial port' };
}

async function kickSerial(cfg: DrawerConfig) {
  const s = serialApi();
  if (!s) throw new Error('This browser cannot talk to a serial port.');
  const ports = await s.getPorts();
  const port = ports.find((p) => { const i = p.getInfo?.() ?? {}; return cfg.vid ? i.usbVendorId === cfg.vid && i.usbProductId === cfg.pid : true; }) ?? ports[0];
  if (!port) throw new Error('No port is connected. Open Drawer setup and choose the port again.');
  await port.open({ baudRate: cfg.baud });
  try { const w = port.writable.getWriter(); await w.write(KICK); w.releaseLock(); await new Promise((r) => setTimeout(r, 150)); } finally { await port.close().catch(() => {}); }
}

function kickPrint() {
  const f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
  f.srcdoc = '<!doctype html><html><head><style>@page{size:80mm 2mm;margin:0}html,body{margin:0;padding:0}</style></head><body>&nbsp;</body></html>';
  f.onload = () => { try { f.contentWindow?.focus(); f.contentWindow?.print(); } finally { setTimeout(() => f.remove(), 5000); } };
  document.body.appendChild(f);
}

/** Open the drawer now. Never throws: a drawer that will not open must not stop a sale. */
export async function openDrawer(cfg: DrawerConfig = loadDrawer()): Promise<{ ok: boolean; message?: string }> {
  try {
    if (cfg.method === 'serial') { await kickSerial(cfg); return { ok: true }; }
    if (cfg.method === 'print') { kickPrint(); return { ok: true }; }
    return { ok: false, message: 'Drawer opening is switched off on this computer.' };
  } catch (e) { return { ok: false, message: e instanceof Error ? e.message : 'The drawer did not open.' }; }
}

/** Called when a sale is taken: opens the drawer if any part of it is paid in cash. */
export function kickForSale(tenders: { method: string; amountCents: number }[]) {
  const cfg = loadDrawer();
  if (cfg.method === 'off' || !cfg.onCash) return;
  if (tenders.some((t) => t.method === 'cash' && t.amountCents > 0)) void openDrawer(cfg);
}
